/**
 * Adaptive Header v5 - Euteyia
 *
 * Fix v5:
 * - Gère les images lazy-loaded (currentSrc / data-srcset)
 * - URL Shopify CDN correcte pour les thumbnails (?width=200)
 * - Flickity: classe .is-selected pour le slide actif
 * - Meilleur fallback si CORS échoue
 */

(function () {
  'use strict';

  const THRESHOLD = 140;

  let header, root;
  let isProductPage    = false;
  let isCollectionPage = false;

  function luma(r, g, b) { return 0.299 * r + 0.587 * g + 0.114 * b; }
  function debounce(fn, ms) {
    let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); };
  }

  // ── Construit une URL thumbnail Shopify CDN ────────────────────────────────
  function shopifyThumb(src, width) {
    if (!src || src.indexOf('cdn.shopify.com') < 0) return src;
    // Ajoute ou remplace le paramètre width
    try {
      const u = new URL(src);
      u.searchParams.set('width', width);
      return u.toString();
    } catch(e) { return src; }
  }

  // ── Trouve la meilleure URL d'une image (gère lazy loading) ───────────────
  function getBestSrc(img) {
    // 1. currentSrc (déjà chargée par le navigateur)
    if (img.currentSrc && img.naturalWidth > 10) return img.currentSrc;
    // 2. data-src (lazy loader)
    if (img.dataset.src && img.dataset.src.indexOf('cdn.shopify.com') >= 0) return img.dataset.src;
    // 3. data-srcset → prend la première URL
    if (img.dataset.srcset) {
      const first = img.dataset.srcset.split(',')[0].trim().split(' ')[0];
      if (first) return first;
    }
    // 4. srcset normal
    if (img.srcset) {
      const first = img.srcset.split(',')[0].trim().split(' ')[0];
      if (first && first.indexOf('cdn.shopify.com') >= 0) return first;
    }
    // 5. src classique
    return img.src || null;
  }

  // ── Sample une image via canvas (CORS) ────────────────────────────────────
  function sampleImage(imgSrc, zoneLeft, zoneRight) {
    return new Promise((resolve) => {
      if (!imgSrc || imgSrc.indexOf('cdn.shopify.com') < 0) return resolve(null);

      const thumbSrc = shopifyThumb(imgSrc, 200);
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        try {
          const iw = img.naturalWidth;
          const ih = img.naturalHeight;
          if (!iw || !ih) return resolve(null);

          // On sample le tiers supérieur de l'image (zone du header)
          const sampleH = Math.max(1, Math.round(ih * 0.15));

          // Zone horizontale proportionnelle
          const screenW = window.innerWidth;
          const pLeft  = Math.max(0, zoneLeft  / screenW);
          const pRight = Math.min(1, zoneRight / screenW);
          const px = Math.round(pLeft  * iw);
          const pw = Math.max(1, Math.round((pRight - pLeft) * iw));

          const c   = document.createElement('canvas');
          c.width   = pw;
          c.height  = sampleH;
          const ctx = c.getContext('2d');
          ctx.drawImage(img, px, 0, pw, sampleH, 0, 0, pw, sampleH);

          const d = ctx.getImageData(0, 0, pw, sampleH).data;
          let sum = 0;
          for (let i = 0; i < d.length; i += 4) {
            sum += luma(d[i], d[i+1], d[i+2]);
          }
          resolve(sum / (d.length / 4));
        } catch (e) { resolve(null); }
      };
      img.onerror = () => resolve(null);
      img.src = thumbSrc;
      setTimeout(() => resolve(null), 4000);
    });
  }

  // ── Trouve l'image active du slider produit ───────────────────────────────
  function getActiveProductImg() {
    const slider = document.querySelector('product-slider');
    if (!slider) return null;
    const active = slider.querySelector('.product-images__slide.is-selected')
                || slider.querySelector('.product-images__slide.is-initial-selected')
                || slider.querySelector('.product-images__slide');
    if (!active) return null;
    return active.querySelector('img');
  }

  // ── Détecte la luminosité pour une zone horizontale ───────────────────────
  async function detectZone(left, right, forceDark) {
    if (forceDark) return 255; // retourne lumineux → pas de changement

    let imgSrc = null;

    if (isProductPage && window.innerWidth >= 768) {
      const img = getActiveProductImg();
      if (img) imgSrc = getBestSrc(img);
    } else if (isCollectionPage) {
      const img = document.querySelector('.collection-header-background img');
      if (img) imgSrc = getBestSrc(img);
    } else {
      // Général: trouve l'image sous le centre du header
      const headerMidY = header.getBoundingClientRect().bottom + 2;
      const cx = (left + right) / 2;
      const el = document.elementFromPoint(cx, headerMidY);
      if (el && !header.contains(el)) {
        // Cherche une img dans l'élément
        function findImg(n, d) {
          if (!n || d > 5) return null;
          if (n.tagName === 'IMG') return n;
          for (const c of n.children) { const f = findImg(c, d+1); if (f) return f; }
          return null;
        }
        const img = findImg(el, 0);
        if (img) imgSrc = getBestSrc(img);

        // Fond CSS sombre
        if (!imgSrc) {
          let node = el;
          while (node && node !== document.body) {
            const bg = window.getComputedStyle(node).backgroundImage;
            if (bg && bg !== 'none' && bg.startsWith('url')) return 60; // sombre présumé
            const bgc = window.getComputedStyle(node).backgroundColor;
            const m = bgc.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?/);
            if (m && (m[4] === undefined || parseFloat(m[4]) > 0.1)) {
              return luma(+m[1], +m[2], +m[3]);
            }
            node = node.parentElement;
          }
          return 255;
        }
      }
    }

    if (!imgSrc) return null;
    return await sampleImage(imgSrc, left, right);
  }

  // ── Applique les variables CSS ────────────────────────────────────────────
  function applyVars(bMenu, bLogo, bIcons) {
    const DARK = 0, LIGHT = 1;
    const toV = (b) => (b !== null && b < THRESHOLD) ? LIGHT : DARK;
    root.style.setProperty('--ha-menu-light',  toV(bMenu));
    root.style.setProperty('--ha-logo-light',  toV(bLogo));
    root.style.setProperty('--ha-icons-light', toV(bIcons));
  }

  function resetVars() {
    ['--ha-menu-light','--ha-logo-light','--ha-icons-light']
      .forEach(v => root.style.setProperty(v, 0));
  }

  // ── Update ────────────────────────────────────────────────────────────────
  async function update() {
    if (header.classList.contains('is-sticky'))  { resetVars(); return; }
    if (header.classList.contains('open-menu'))  { resetVars(); return; }

    const isMobile = window.innerWidth < 768;

    if (isMobile) {
      const b = await detectZone(0, window.innerWidth, false);
      const v = (b !== null && b < THRESHOLD) ? 1 : 0;
      ['--ha-menu-light','--ha-logo-light','--ha-icons-light']
        .forEach(cv => root.style.setProperty(cv, v));
      return;
    }

    // Desktop — 3 zones
    const menuEl  = header.querySelector('.full-menu');
    const logoEl  = header.querySelector('.logolink');
    const iconsEl = header.querySelector('.thb-secondary-area');

    function zb(el) {
      if (!el) return null;
      const r = el.getBoundingClientRect();
      return [r.left, r.right];
    }

    const mb = zb(menuEl);
    const lb = zb(logoEl);
    const ib = zb(iconsEl);

    // Page produit desktop : icônes toujours noires (panneau produit blanc à droite)
    const forceIconsDark = isProductPage && window.innerWidth >= 768;

    const [bM, bL, bI] = await Promise.all([
      mb ? detectZone(mb[0], mb[1], false)          : Promise.resolve(null),
      lb ? detectZone(lb[0], lb[1], false)          : Promise.resolve(null),
      ib ? detectZone(ib[0], ib[1], forceIconsDark) : Promise.resolve(null),
    ]);

    applyVars(bM, bL, bI);
  }

  // ── Init ──────────────────────────────────────────────────────────────────
  function init() {
    header = document.querySelector('#header');
    root   = document.documentElement;
    if (!header) return;

    isProductPage    = document.body.classList.contains('template-product');
    isCollectionPage = document.body.classList.contains('template-collection');

    resetVars();

    new MutationObserver(() => update())
      .observe(header, { attributes: true, attributeFilter: ['class'] });

    const dUpdate = debounce(update, 60);
    window.addEventListener('scroll', dUpdate, { passive: true });
    window.addEventListener('resize', dUpdate, { passive: true });

    // Hook Flickity sur tous les sliders
    function hookSlider(slider) {
      const poll = setInterval(() => {
        if (typeof Flickity === 'undefined') return;
        const flkty = Flickity.data(slider);
        if (flkty && flkty.slides.length) {
          clearInterval(poll);
          flkty.on('select', () => setTimeout(update, 200));
        }
      }, 100);
    }
    document.querySelectorAll('product-slider, slide-show').forEach(hookSlider);

    // Calculs
    update();
    window.addEventListener('load', () => { update(); setTimeout(update, 800); });
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init)
    : init();
})();